import classNames from 'classnames';
import React, {
    ReactElement,
    useState,
    useEffect,
} from 'react';

import { IQATestableProps } from '../../shared/types/IQATestableProps';
import { ISetState } from '../../shared/types/ISetState';
import { IStyleableProps } from '../../shared/types/IStyleableProps';
import { getRandomId } from '../utils/getRandomId';

import { HelpTipIcon } from './HelpTipIcon';

export type ITextInputProps = IQATestableProps & IStyleableProps & {
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onBlur?: (e: React.ChangeEvent<HTMLInputElement>) => void;
    disabled?: boolean;
    error?: string;
    hint?: string;
    value?: string;
    placeholder?: string;
    label: string;
    type?: string;
    name?: string;
    help?: string;
};

export function OzTextInput (props: ITextInputProps): ReactElement {
    const {
        error,
        hint,
        disabled = false,
        onChange,
        onBlur,
        value = '',
        placeholder = '',
        label = '',
        'data-testid': dataTest = '',
        className = '',
        type = 'text',
        name = '',
        help = '',
    }: ITextInputProps = props;

    const [isFocused, setIsFocused]: [boolean, ISetState<boolean>] = useState<boolean>(false);
    const [inputValue, setInputValue]: [string, ISetState<string>] = useState<string>(value || '');

    useEffect((): void => {
        setInputValue(value);
    }, [value]);

    const wrapperClassName: string = classNames({
        'w-full h-8 flex flex-col justify-center items-center': true,
        'relative border border-gray-300 rounded bg-white text-black-75': true,
        '!border-red-500': (error && !disabled),
        '!border-teal-500': (isFocused && !error),
        '!bg-gray-100': disabled,
    });

    const inputClassName: string = classNames({
        'w-full bg-transparent text-med text-gray-600 font-medium': true,
        'leading-relaxed outline-none caret-primary px-2 text-ellipsis': true,
        'text-gray-900': (isFocused || !!inputValue),
        'text-secondary-contrast caret-error': (!!error && !disabled),
        'pr-7': !!help,
    });

    const labelClassName: string = classNames({
        'w-full mb-1/2 block text-xs text-left text-gray-700': true,
        '!font-medium truncate opacity-100 leading-4': true,
        'transition-opacity ease-in-out duration-500 px-2': true,
        'hidden flex opacity-00': (!isFocused && !inputValue), // Hides the label
        'pr-7': !!help,
    });

    const helpTipIconClassName: string = classNames({
        'absolute align-middle right-2 text-gray-400 fill-current': true,
        'hidden': !help,
    });

    const errorClassName: string = classNames({
        'w-full text-base font-normal truncate leading-12 pt-1/2': true,
        'text-red-500': !!error,
    });

    const hintClassName: string = classNames({
        'w-full text-base font-normal truncate leading-12 pt-1/2': true,
        'text-gray-700': !!hint,
    });

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setInputValue(event.target.value);
        onChange(event);
    };

    const handleBlur = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setIsFocused(false);

        if (onBlur) {
            onBlur(event);
        }
    };

    const handleFocus = (): void => {
        setIsFocused(true);
    };

    let placeholderValue: string = placeholder;

    if (isFocused) {
        placeholderValue = '';
    }

    const id: string = name || dataTest || getRandomId();

    return (
        <div className={`h-auto ${className}`} data-testid={dataTest}>
            <div className={wrapperClassName}>
                <div className="w-full align-middle">
                    <label
                        className={labelClassName}
                        htmlFor={id}
                    >
                        {label}
                    </label>
                    <input
                        id={id}
                        name={name}
                        type={type}
                        className={inputClassName}
                        disabled={disabled}
                        placeholder={placeholderValue}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={inputValue}
                    />
                </div>
                <HelpTipIcon
                    className={helpTipIconClassName}
                    title={help}
                />
            </div>
            {hint && !error &&
                <p className={hintClassName}>{hint}</p>}
            {error &&
                <p className={errorClassName}>{error}</p>}
        </div>
    );
}
